import openaiService from './openaiService';
import contentService from './contentService';

class AIChatService {
  // Create a new chat conversation
  async createChat(userId, title = 'New Chat') {
    try {
      // In a real implementation, you would save this to the database
      const chatId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      return {
        data: {
          id: chatId,
          title,
          userId,
          createdAt: new Date().toISOString(),
          messages: []
        },
        error: null
      };
    } catch (error) {
      console.error('Create Chat Error:', error);
      return {
        data: null,
        error: error.message
      };
    }
  }

  // Send a message and get AI response
  async sendMessage(chatId, message, context = [], conversationHistory = [], webResults = []) {
    try {
      // Build context from selected sources with IDs for citation
      let contextPrompt = '';
      let contextItemsList = [];
      
      if (context.length > 0) {
        contextItemsList = context.map((item, index) => {
          // Use transcript, visual description, or summary
          const contentText = item.transcription || 
                            (item.visual_tags?.length > 0 ? `Visual elements: ${item.visual_tags.join(', ')}` : '') ||
                            item.summary || 
                            item.description ||
                            'No content available';
          
          // Build rich context description with ID for citation
          let itemContext = `[Item ID: ${item.id}]\nTitle: ${item.title || 'Untitled'}`;
          if (item.description) itemContext += `\nDescription: ${item.description}`;
          // Use file_url if available (for attachments), otherwise fall back to url
          const fileUrl = item.file_url || item.url;
          if (fileUrl) itemContext += `\nURL: ${fileUrl}`;
          if (item.content_type || item.file_type) itemContext += `\nType: ${item.file_type || item.content_type}`;
          if (item.visual_tags && item.visual_tags.length > 0) {
            itemContext += `\nVisual elements: ${item.visual_tags.join(', ')}`;
          }
          itemContext += `\nContent: ${contentText}`;
          
          return {
            id: item.id,
            context: itemContext,
            title: item.title || 'Untitled',
            url: fileUrl
          };
        });
        
        const contextText = contextItemsList.map(item => item.context).join('\n\n---\n\n');
        contextPrompt = `\n\nRelevant content from ${context.length} attached file(s) or content item(s):\n\n${contextText}`;
      }

      // Add web results if available
      let webResultsPrompt = '';
      if (webResults.length > 0) {
        const webResultsText = webResults.map((result, index) => {
          return `[Web Result ID: web_${index}]\nTitle: ${result.title || 'Web Result'}\nURL: ${result.url || ''}\nSnippet: ${result.snippet || result.description || ''}`;
        }).join('\n\n---\n\n');
        webResultsPrompt = `\n\nWeb search results:\n\n${webResultsText}`;
      }

      // Prepare conversation history for context
      const history = conversationHistory.slice(-6).map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

      // Create system prompt with structured JSON response requirements
      let systemPrompt = `You are a helpful AI assistant for Swirl, a personal intelligence platform. You help users analyze and understand their content. Provide insightful, helpful responses based on the context provided.

IMPORTANT: You must respond with a valid JSON object following this exact structure:
{
  "answer": "Your main answer to the user's question",
  "citations": [
    {
      "sourceId": "item_id_or_web_result_id",
      "type": "swirl_item" or "web",
      "label": "Short descriptive label",
      "url": "URL if available (for web sources)"
    }
  ],
  "summary": "A brief 1-2 sentence summary of your answer",
  "followUps": ["Suggested follow-up question 1", "Suggested follow-up question 2", "Suggested follow-up question 3"]
}

CITATION RULES:
- Reference context items by their ID (e.g., "item_id") with type "swirl_item"
- Reference web results by their ID (e.g., "web_0") with type "web" and include the URL
- Only cite sources you actually used in your answer
- Provide 2-4 suggested follow-up questions that would be helpful next steps`;
      
      // If context is provided, enhance the system prompt to be aware of attachments
      if (context.length > 0) {
        systemPrompt += `\n\nThe user has provided ${context.length} file(s) or content item(s) as context. When the user refers to "these", "them", "the files", "the images", or similar phrases, they are referring to the attached content. Always analyze and reference the provided context when answering questions.`;
      }

      // Make API call to OpenAI with JSON response format
      const response = await openaiService.createChatCompletion([
        { role: 'system', content: systemPrompt },
        ...history,
        { role: 'user', content: message + contextPrompt + webResultsPrompt }
      ], {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 2000,
        response_format: { type: 'json_object' }
      });

      if (response.error) {
        throw new Error(response.error);
      }

      // Parse JSON response
      let parsed;
      try {
        const responseText = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
        // Extract JSON from markdown code blocks if present
        const jsonMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/) || 
                         responseText.match(/```\s*([\s\S]*?)\s*```/);
        const jsonText = jsonMatch ? jsonMatch[1].trim() : responseText.trim();
        parsed = JSON.parse(jsonText);
      } catch (parseError) {
        console.warn('[AIChatService] Failed to parse JSON response, using fallback:', parseError);
        // Fallback: treat whole response as answer
        parsed = {
          answer: typeof response.data === 'string' ? response.data : JSON.stringify(response.data),
          citations: [],
          summary: null,
          followUps: []
        };
      }

      // Map context item IDs to full citation objects
      const mappedCitations = (parsed.citations || []).map(citation => {
        // Check if it's a context item
        const contextItem = contextItemsList.find(item => item.id === citation.sourceId);
        if (contextItem) {
          return {
            sourceId: citation.sourceId,
            type: 'swirl_item',
            label: citation.label || contextItem.title,
            url: citation.url || contextItem.url
          };
        }
        // Check if it's a web result
        const webIndex = citation.sourceId?.replace('web_', '');
        if (webIndex !== undefined && webResults[parseInt(webIndex)]) {
          const webResult = webResults[parseInt(webIndex)];
          return {
            sourceId: citation.sourceId,
            type: 'web',
            label: citation.label || webResult.title || 'Web Result',
            url: citation.url || webResult.url
          };
        }
        // Return as-is if already properly formatted
        return citation;
      });

      return {
        data: {
          id: `msg_${Date.now()}`,
          type: 'ai',
          content: parsed.answer || '',
          citations: mappedCitations,
          summary: parsed.summary || null,
          followUps: parsed.followUps || [],
          timestamp: new Date().toISOString()
        },
        error: null
      };
    } catch (error) {
      console.error('Send Message Error:', error);
      return {
        data: null,
        error: error.message
      };
    }
  }

  // Get context content based on selected sources
  async getContextContent(selectedSources, userId) {
    try {
      if (!selectedSources || selectedSources.includes('All')) {
        return { data: [], error: null };
      }

      const content = [];
      
      // Load content from selected sources
      for (const source of selectedSources) {
        if (source.startsWith('collection-')) {
          const collectionId = source.replace('collection-', '');
          const { data: collectionContent } = await contentService.getContentByCollection(userId, collectionId);
          if (collectionContent) content.push(...collectionContent);
        } else if (source.startsWith('channel-')) {
          const channel = source.replace('channel-', '');
          const { data: channelContent } = await contentService.getContentByChannel(userId, channel);
          if (channelContent) content.push(...channelContent);
        }
      }
      
      return { data: content, error: null };
    } catch (error) {
      console.error('Get Context Content Error:', error);
      return {
        data: [],
        error: error.message
      };
    }
  }

  // Save chat to database (placeholder for future implementation)
  async saveChat(chatId, messages) {
    try {
      // In a real implementation, you would save this to Supabase
      console.log('Saving chat:', chatId, messages.length, 'messages');
      return { data: true, error: null };
    } catch (error) {
      console.error('Save Chat Error:', error);
      return {
        data: false,
        error: error.message
      };
    }
  }

  // Load chat from database (placeholder for future implementation)
  async loadChat(chatId) {
    try {
      // In a real implementation, you would load from Supabase
      return {
        data: {
          id: chatId,
          title: 'Loaded Chat',
          messages: [],
          createdAt: new Date().toISOString()
        },
        error: null
      };
    } catch (error) {
      console.error('Load Chat Error:', error);
      return {
        data: null,
        error: error.message
      };
    }
  }

  // Get chat history for user
  async getChatHistory(userId) {
    try {
      // In a real implementation, you would load from Supabase
      return {
        data: [],
        error: null
      };
    } catch (error) {
      console.error('Get Chat History Error:', error);
      return {
        data: [],
        error: error.message
      };
    }
  }
}

export default new AIChatService();
